import { useEffect, useRef, useState } from 'react';
import { getLinkPreview } from '../api/messages';
import { dividiLink, primoLink } from '../utils/link';

// Il testo di un messaggio con i link cliccabili. Si aprono in una scheda
// nuova, senza passare a quel sito né la pagina di provenienza né l'accesso
// alla finestra dell'app.
// `children` va in fondo al testo, sulla stessa riga: è il posto riservato
// all'ora del fumetto (vedi ChatHistory).
export function TestoConLink({ testo, className = 'whitespace-pre-wrap break-words', children = null }) {
  return <p className={className}>{dividiLink(testo).map((pezzo, i) => pezzo.link
    ? <a key={i} href={pezzo.link} target="_blank" rel="noopener noreferrer nofollow"
        className="underline text-info break-all" onClick={e => e.stopPropagation()}>{pezzo.link}</a>
    : pezzo.testo)}{children}</p>;
}

// Le anteprime si chiedono al server due alla volta e una volta sola per
// messaggio: una chat piena di link non deve occupare tutti i worker.
const giaLette = new Map();
const coda = [];
let inCorso = 0;
const PARALLELE = 2;

// Anche un errore immediato diventa «nessuna anteprima», non un guasto, e il
// posto nella coda si libera comunque: altrimenti un errore la fermerebbe.
function leggi({ id, risolvi }) {
  Promise.resolve().then(() => getLinkPreview(id))
    .then(risposta => risposta?.data?.anteprima || null)
    .catch(() => null)
    .then(anteprima => { inCorso -= 1; risolvi(anteprima); prossima(); });
}

function prossima() {
  while (inCorso < PARALLELE && coda.length) {
    inCorso += 1;
    leggi(coda.shift());
  }
}

export function chiediAnteprima(id) {
  if (!giaLette.has(id)) {
    giaLette.set(id, new Promise(risolvi => { coda.push({ id, risolvi }); prossima(); }));
  }
  return giaLette.get(id);
}

// La scheda sotto il messaggio: immagine, sito, titolo, descrizione. La chiede
// solo quando il fumetto entra nello schermo.
export function AnteprimaLink({ messaggio }) {
  const [anteprima, setAnteprima] = useState(null);
  const nodo = useRef(null);
  const link = messaggio.id ? primoLink(messaggio.body) : null;
  useEffect(() => {
    if (!link) return undefined;
    let vivo = true;
    const carica = () => chiediAnteprima(messaggio.id).then(dati => { if (vivo) setAnteprima(dati); });
    if (typeof IntersectionObserver === 'undefined' || !nodo.current) { carica(); return () => { vivo = false; }; }
    const osservatore = new IntersectionObserver(voci => {
      if (voci.some(v => v.isIntersecting)) { osservatore.disconnect(); carica(); }
    }, { rootMargin: '200px' });
    osservatore.observe(nodo.current);
    return () => { vivo = false; osservatore.disconnect(); };
  }, [link, messaggio.id]);
  if (!link) return null;
  if (!anteprima) return <span ref={nodo} aria-hidden="true" />;
  return <a href={anteprima.url} target="_blank" rel="noopener noreferrer nofollow"
    onClick={e => e.stopPropagation()}
    className="mt-2 block overflow-hidden rounded-md border border-line bg-surface-2 no-underline max-w-[320px]">
    {anteprima.immagine && <img src={anteprima.immagine} alt="" className="block w-full max-h-44 object-cover" />}
    <span className="block px-3 py-2">
      {anteprima.sito && <span className="block text-[11px] uppercase tracking-wide text-muted truncate">{anteprima.sito}</span>}
      {anteprima.titolo && <span className="block text-sm font-medium text-ink line-clamp-2">{anteprima.titolo}</span>}
      {anteprima.descrizione && <span className="block text-xs text-muted line-clamp-2">{anteprima.descrizione}</span>}
    </span>
  </a>;
}
